import type { CustomerRevenueReportItem } from '@vy/types';
import { MONTHS } from '@vy/types';
import type { Workbook, Worksheet } from 'exceljs';
import {
  computeWaterfallTotals,
  type WaterfallTotals,
} from '../../../customer-revenue-report-page/customer-revenue-report-components/CustomerRevenueReportTable/compute-waterfall-totals';
import { parseDateHeader } from '../../../customer-revenue-report-page/customer-revenue-report-components/CustomerRevenueReportTable/customer-revenue-report-calculations';

const WATERFALL_SUMMARY_ROWS = [
  { group: 'Deferred', metricKey: 'newDeferredRev' as const, label: 'New Deferred Rev' },
  { group: 'Deferred', metricKey: 'recognizedRevenueDeferred' as const, label: 'Recognized Revenue (Deferred)' },
  { group: 'Deferred', metricKey: 'endingDeferredBalance' as const, label: 'Ending Deferred Balance' },
  { group: 'Unbilled', metricKey: 'newUnbilledAR' as const, label: 'New Unbilled AR' },
  { group: 'Unbilled', metricKey: 'recognizedRevenueUnbilled' as const, label: 'Recognized Revenue (Unbilled)' },
  { group: 'Unbilled', metricKey: 'endingUnbilledAR' as const, label: 'Ending Unbilled AR' },
] as const;

type SummaryRow = (typeof WATERFALL_SUMMARY_ROWS)[number];

export function buildWaterfallSummarySheet(
  workbook: Workbook,
  searchedItems: CustomerRevenueReportItem[],
  dateHeaders: string[],
): void {
  const worksheet = workbook.addWorksheet('Waterfall Summary');
  const waterfallTotals = computeWaterfallTotals(searchedItems);

  writeSummaryHeader(worksheet, dateHeaders);
  writeSummaryRows(worksheet, waterfallTotals, dateHeaders);
  applySummaryColumnStyles(worksheet, dateHeaders);
}

function writeSummaryHeader(worksheet: Worksheet, dateHeaders: string[]): void {
  const headerRow = ['Group', 'Metric', ...dateHeaders];
  worksheet.addRow(headerRow);
  worksheet.getRow(1).font = { bold: true };
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: headerRow.length },
  };
}

function writeSummaryRows(
  worksheet: Worksheet,
  waterfallTotals: WaterfallTotals,
  dateHeaders: string[],
): void {
  for (const summaryRow of WATERFALL_SUMMARY_ROWS) {
    const dateValues = dateHeaders.map((dateHeader) =>
      getSummaryMetricValue(waterfallTotals, summaryRow, dateHeader),
    );
    const excelRow = worksheet.addRow([summaryRow.group, summaryRow.label, ...dateValues]);
    excelRow.commit();
  }
}

function getSummaryMetricValue(
  waterfallTotals: WaterfallTotals,
  summaryRow: SummaryRow,
  dateHeader: string,
): number | null {
  const headerDate = parseDateHeader(dateHeader);
  const year = String(headerDate.getFullYear());
  const month = MONTHS[headerDate.getMonth()];
  const breakdown = waterfallTotals[year]?.[month as keyof (typeof waterfallTotals)[string]];
  const value = breakdown?.[summaryRow.metricKey] ?? 0;
  return value === 0 ? null : value;
}

function applySummaryColumnStyles(worksheet: Worksheet, dateHeaders: string[]): void {
  worksheet.getColumn(1).width = 14;
  worksheet.getColumn(2).width = 36;

  for (let index = 0; index < dateHeaders.length; index++) {
    const column = worksheet.getColumn(3 + index);
    column.width = 16;
    column.numFmt = '#,##0.00';
    column.alignment = { horizontal: 'right' };
  }
}
